import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import cn from 'classnames';
import isEmpty from 'ramda/src/isEmpty';
import color from 'color';
import colorString from 'color-string';
import Button from '@fountain/ui-kit/components/button';
import Routes from 'apps/portal/routes.erb';
import { getCustomPropertiesFromObject } from 'helpers/shared';
import generateAxPalette from 'helpers/ax-palette';
import AxHeader from '../../components/ax-header';

import './styles.pcss';

const TOO_DARK_LUMINOSITY = 0.15;

const normalizeColor = (value) => {
  const hslView = value.hsl();
  if (value.luminosity() < TOO_DARK_LUMINOSITY) {
    hslView.color[2] += 20;
  }
  return hslView;
};

export default class OpeningsDirectoryLayout extends PureComponent {
  static propTypes = {
    ax: PropTypes.bool,
    axPalette: PropTypes.shape({
      primary: PropTypes.string,
      secondary: PropTypes.string,
      grey1: PropTypes.string,
      grey2: PropTypes.string,
      grey3: PropTypes.string,
      grey4: PropTypes.string,
      grey5: PropTypes.string,
      grey6: PropTypes.string,
      boxShadow1: PropTypes.string,
      boxShadow2: PropTypes.string,
      inputInset: PropTypes.string,
      inputOutline: PropTypes.string,
      white: PropTypes.string,
    }),
    brand: PropTypes.shape({
      header: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      logo: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      name: PropTypes.string,
      styles: PropTypes.shape({
        buttonColor: PropTypes.string,
        headerBgColor: PropTypes.string,
        headerTextColor: PropTypes.string,
        linkColor: PropTypes.string,
        navbarBgColor: PropTypes.string,
      }),
      headerText: PropTypes.string,
      optionalDescription: PropTypes.string,
    }),
    children: PropTypes.node.isRequired,
    countryName: PropTypes.string,
    privacyPolicyEnabled: PropTypes.bool,
    privacyPolicyUrl: PropTypes.string,
    privacyPolicyLinkLabel: PropTypes.string,
    termsOfServiceLinkLabel: PropTypes.string,
  }

  static defaultProps = {
    ax: false,
    axPalette: {},
    brand: {},
    countryName: null,
    privacyPolicyEnabled: false,
    privacyPolicyUrl: null,
    privacyPolicyLinkLabel: null,
    termsOfServiceLinkLabel: null,
  }

  _getLigtnessOffset = (value) => {
    const offsetFromExtemeLightness = 50 - Math.abs(50 - value.hsl().color[2]);
    const inverseLightness = offsetFromExtemeLightness > 0 ? Math.sqrt(offsetFromExtemeLightness / 3) : 0.2;
    const offset = inverseLightness / offsetFromExtemeLightness;

    return value.isDark() ? offset : (-1 * offset);
  }

  _formatColor = value => value.hsl().string()

  _getDefaultColor = (value) => {
    if (!this._isColor(value)) {
      return null;
    }

    return value;
  }

  _getHoverColor = (value) => {
    if (!this._isColor(value)) {
      return null;
    }

    const col = normalizeColor(color(value));
    const offset = this._getLigtnessOffset(col);

    return this._formatColor(col.lighten(offset));
  }

  _getActiveColor = (value) => {
    if (!this._isColor(value)) {
      return null;
    }

    const col = normalizeColor(color(value));
    const offset = this._getLigtnessOffset(col);

    return this._formatColor(col.lighten(offset * 1.5));
  }

  _getDisabledColor = (value) => {
    if (!this._isColor(value)) {
      return null;
    }
    return this._formatColor(color(value).fade(0.4));
  }

  _isColor = value => value && colorString.get(value)

  _renderHelmet = () => {
    let { axPalette } = this.props;
    const { brand, ax } = this.props;

    if (axPalette && Object.keys(axPalette).length < 1) {
      // eslint-disable-next-line prefer-destructuring
      axPalette = brand.axPalette;
    }

    if (!brand || isEmpty(brand)) {
      return false;
    }

    if (!ax && (!brand.styles || isEmpty(brand.styles))) {
      return false;
    }

    const styleObj = ax && axPalette ? generateAxPalette(axPalette) : {
      '--button-primary-default': this._getDefaultColor(brand.styles.buttonColor),
      '--button-primary-hover': this._getHoverColor(brand.styles.buttonColor),
      '--button-primary-active': this._getActiveColor(brand.styles.buttonColor),
      '--button-primary-disabled': this._getDisabledColor(brand.styles.buttonColor),
      '--button-link-default': brand.styles.linkColor,
      '--button-link-hover': this._getHoverColor(brand.styles.linkColor),
      '--button-link-active': this._getActiveColor(brand.styles.linkColor),
      '--button-link-disabled': this._getDisabledColor(brand.styles.linkColor),
      '--header-bg-color': brand.styles.headerBgColor,
      '--header-text-color': brand.styles.headerTextColor,
      '--link-default': brand.styles.linkColor,
      '--link-hover': this._getHoverColor(brand.styles.linkColor),
      '--link-active': this._getActiveColor(brand.styles.linkColor),
      '--link-disabled': this._getDisabledColor(brand.styles.linkColor),
      '--navbar-bg-color': brand.styles.navbarBgColor,
    };

    return (
      <Helmet>
        <style type="text/css">
          {getCustomPropertiesFromObject(styleObj)}
        </style>
      </Helmet>
    );
  }

  _renderHeader = () => {
    const {
      brand,
      ax,
      countryName,
    } = this.props;
    const className = cn('openings-directory-layout__header', {
      'openings-directory-layout__header_empty': !brand.header,
      'openings-directory-layout__header-has-optional-description': brand.optionalDescription && brand.optionalDescription.length > 0,
    });
    const style = brand.header ? { backgroundImage: `url(${brand.header})` } : {};
    const { headerText } = brand;

    const header = ax ? (
      <AxHeader
        countryName={countryName}
        logo={brand.squareLogo || brand.logo}
        brand={brand}
        headerText={headerText}
        optionalDescription={brand.optionalDescription}
      />
    ) : (
      <div className={className} style={style}>
        {headerText && <h1 className="openings-directory-layout__header-text">{headerText}</h1>}
        {brand.optionalDescription && <h5 className="openings-directory-layout__header-optional-description">{brand.optionalDescription}</h5>}
      </div>
    );

    return header;
  }

  _renderPrivacyLinks = (showLeadingSeparator) => {
    const {
      privacyPolicyEnabled,
      privacyPolicyUrl,
      privacyPolicyLinkLabel,
      termsOfServiceLinkLabel,
    } = this.props;

    if (!privacyPolicyEnabled || !privacyPolicyUrl) return null;

    return (
      <Fragment>
        {showLeadingSeparator && <span className="openings-directory-layout__footer-sep" aria-hidden="true">|</span>}
        <a className="openings-directory-layout__footer-link" href={`${privacyPolicyUrl}#privacy-policy`}>{privacyPolicyLinkLabel}</a>
        <span className="openings-directory-layout__footer-sep" aria-hidden="true">|</span>
        <a className="openings-directory-layout__footer-link" href={`${privacyPolicyUrl}#terms-of-service`}>{termsOfServiceLinkLabel}</a>
      </Fragment>
    );
  }

  render() {
    const { brand, ax, privacyPolicyEnabled } = this.props;

    const header = ax ? null : (
      <div className="openings-directory-layout__nav">
        {brand.logo ? (
          <img
            alt={brand.name}
            src={brand.logo}
            className="openings-directory-layout__nav-logo"
          />
        ) : (
          <h3 className="openings-directory-layout__nav-brand-name">{brand.name}</h3>
        )}

        <Button
          className="openings-directory-layout__nav-link"
          href={Routes.new_identity_session_path()}
          rel="noopener noreferrer"
          target="_blank"
          theme="text"
        >
          Looking for your application?
        </Button>
      </div>
    );

    const showPoweredByFountain = !ax;
    const footer = (showPoweredByFountain || privacyPolicyEnabled) ? (
      <div className="openings-directory-layout__footer">
        {showPoweredByFountain && (
          <span className="openings-directory-layout__footer-text">Powered by Fountain</span>
        )}
        {this._renderPrivacyLinks(showPoweredByFountain)}
      </div>
    ) : null;

    const isPicker = window.location.pathname.indexOf('/picker') !== -1;

    return (
      <Fragment>
        {this._renderHelmet()}

        <div className={cn(
            'openings-directory-layout',
            { 'ax-non-picker-layout': ax && !isPicker }
          )}
        >
          {header}
          <div
            className={cn(
              'openings-directory-layout__content',
              { 'openings-directory-layout__ax': ax }
            )}
          >
            {this._renderHeader()}
            {this.props.children}
          </div>
          {footer}
        </div>
      </Fragment>
    );
  }
}
